import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import { createAssetInfo } from '@/features/Downloads/create-asset-info-for-upsell';
import { getAccountTypes } from '@/features/Downloads/get-account-types-for-upsell';
import { useHasRole } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { recordConversion } from '@/utils/analytics/conversion';

import {
  assetInfoAtom,
  compactAtom,
  conversionPointAtom,
  currentAccountTypeAtom,
  currentCreateAccountTypeAtom,
  hasNoDownloadsLeftAtom,
  isUpsellOpenAtom,
  reasonAtom,
  upsellAtom,
  userInfoAtom,
} from './internal/upsellAtom';

import type { AssetGroup, AssetType } from '@/types/asset';
import type { UpsellReason } from './types';

/**
 * Public surface for the gateway's upsell-modal mechanics. Callers go
 * through `useAssetUpsellTrigger` to open the modal and through
 * `useUpsellModalState` / `useCloseUpsellModal` to read or close it.
 *
 * The mobile drawer-animation buffer (the 300 ms wait between the source
 * drawer dismissing and the upsell modal opening) lives inside `trigger()`
 * — without it the two animations collide and produce a focus-trap fight
 * on mobile. Keeping the buffer here means no caller can forget it.
 */

interface AssetUpsellOptions {
  /** Render the compact plan picker (DEV-7218 variant). */
  compact?: boolean;
  hasNoDownloadsLeft: boolean;
  /** Called before the upsell modal opens. Lets the caller close its own dialog. */
  onBeforeOpen?: () => void;
  /** Why the gateway gated the download — see {@link UpsellReason}. */
  reason: UpsellReason;
}

/**
 * Map the gateway gate `reason` (+ asset kind) to the granular conversion
 * point used for purchase attribution.
 *
 * `no-credits` is the download-limit case. Everything else is a
 * premium-capability upsell: a premium asset, a WAV (`format-locked`) or a
 * 4K (`resolution-locked`) grab. Those three fold into the generic premium
 * point (motion graphics — incl. LUTs — keep their own), because the backend
 * only has `DownloadPremium` / `DownloadPremiumMg` to attribute them to. The
 * finer format/resolution split lives on the `modal_screen_view` event via
 * the raw `reason`, so nothing is lost — see `UpsellState.reason`.
 */
export const conversionPointForUpsell = (
  reason: UpsellReason,
  assetType: AssetType | undefined
): ModalConversionPoints => {
  if (reason === 'no-credits') {
    return ModalConversionPoints.DownloadLimit;
  }
  return assetType === 'motion'
    ? ModalConversionPoints.DownloadPremiumMg
    : ModalConversionPoints.DownloadPremium;
};

/**
 * Hook returning a function that writes the upsell state for an asset and
 * opens the modal. Encapsulates the role-derived account-type projection,
 * the credit reconciliation, and the mobile drawer-animation timing buffer.
 */
export const useAssetUpsellTrigger = () => {
  const { settings } = useSettings();
  const setUpsellState = useSetAtom(upsellAtom);
  const setIsUpsellOpen = useSetAtom(isUpsellOpenAtom);
  const { isSm } = useTWScreens();

  // Read role flags directly (rather than going through the snapshot) so this
  // hook can be called from a wider set of consumers without coupling them to
  // useDownloadGateway. Decisions are still permission-driven elsewhere;
  // roles here only project the upsell payload's account-type fields.
  const isPremium = useHasRole('Premium');
  const isBusiness = useHasRole('Business');
  const isEssentials = useHasRole('Essentials');
  const isCreator = useHasRole('Creator');
  const isPro = useHasRole('Pro');

  return useCallback(
    (asset: AssetGroup, options: AssetUpsellOptions) => {
      const {
        compact = false,
        hasNoDownloadsLeft,
        reason,
        onBeforeOpen,
      } = options;

      const { accountType, createAccountType } = getAccountTypes({
        isPremium,
        isBusiness,
        isCreator,
        isEssentials,
        isPro,
      });

      const assetInfo = createAssetInfo(asset);
      const conversionPoint = conversionPointForUpsell(
        reason,
        assetInfo.assetType
      );

      // Record last-touch attribution the moment the paywall opens, so the
      // eventual purchase splits into `goPremium_downloadLimit` /
      // `_premiumTrack` / `_downloadPremiumMg` — for free users (Stripe
      // checkout) and existing subscribers (upgrade modal) alike. The Subscribe
      // click in the modal re-records the same point via `openUpsell`.
      recordConversion({
        source: conversionPoints[conversionPoint],
        assetId: Number(asset.data.id),
        assetType: assetInfo.assetType,
      });

      setUpsellState((prev) => ({
        ...prev,
        assetInfo,
        userInfo: {
          ...prev.userInfo,
          remainingTokens: hasNoDownloadsLeft
            ? 0
            : (settings?.credits?.credits_current ?? 0),
          isPremium: isCreator || isPremium,
          isBusiness: isPro || isBusiness,
        },
        hasNoDownloadsLeft,
        reason,
        compact,
        conversionPoint,
        currentAccountType: accountType,
        currentCreateAccountType: createAccountType,
      }));

      onBeforeOpen?.();

      // Mobile: the dismiss animation on the source drawer takes ~300 ms.
      // Opening the upsell modal mid-animation causes a visual stutter and
      // (historically) a focus-trap conflict. Desktop modals don't have
      // this transition so we open synchronously.
      if (isSm) {
        setIsUpsellOpen(true);
      } else {
        setTimeout(() => {
          setIsUpsellOpen(true);
        }, 300);
      }
    },
    [
      settings?.credits?.credits_current,
      isCreator,
      isPremium,
      isPro,
      isBusiness,
      isEssentials,
      setUpsellState,
      setIsUpsellOpen,
      isSm,
    ]
  );
};

/**
 * Read-only view of the current upsell state — for the UpsellModal UI.
 *
 * Memoised so the returned object identity is stable across renders that
 * don't change any of the underlying atoms; otherwise downstream
 * `useMemo` / `useEffect` deps that reference it would trigger every
 * render.
 */
export const useUpsellModalState = () => {
  const isOpen = useAtomValue(isUpsellOpenAtom);
  const assetInfo = useAtomValue(assetInfoAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const hasNoDownloadsLeft = useAtomValue(hasNoDownloadsLeftAtom);
  const reason = useAtomValue(reasonAtom);
  const compact = useAtomValue(compactAtom);
  const conversionPoint = useAtomValue(conversionPointAtom);
  const currentAccountType = useAtomValue(currentAccountTypeAtom);
  const currentCreateAccountType = useAtomValue(currentCreateAccountTypeAtom);

  return useMemo(
    () => ({
      isOpen,
      assetInfo,
      userInfo,
      hasNoDownloadsLeft,
      reason,
      compact,
      conversionPoint,
      currentAccountType,
      currentCreateAccountType,
    }),
    [
      isOpen,
      assetInfo,
      userInfo,
      hasNoDownloadsLeft,
      reason,
      compact,
      conversionPoint,
      currentAccountType,
      currentCreateAccountType,
    ]
  );
};

/** Imperative close for the UpsellModal — sets isOpen to false. */
export const useCloseUpsellModal = () => {
  const setIsUpsellOpen = useSetAtom(isUpsellOpenAtom);
  return useCallback(() => setIsUpsellOpen(false), [setIsUpsellOpen]);
};

/** Bare read for "is the upsell modal currently open?" — used by ConfirmDownloadModal. */
export const useIsUpsellModalOpen = () => useAtomValue(isUpsellOpenAtom);
